'use es6';

import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { useSelector, useDispatch } from 'react-redux';
import { useState, useEffect } from 'react';
import { NavMarker } from 'react-rhumb';
import HR from 'UIComponents/elements/HR';
import UIButton from 'UIComponents/button/UIButton';
import UIFlex from 'UIComponents/layout/UIFlex';
import UITruncateString from 'UIComponents/text/UITruncateString';
import UIAccordionItem from 'UIComponents/accordion/UIAccordionItem';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { parse as parseQuery, stringify } from 'hub-http/helpers/params';
import HubSpotOnlyAppCookieLogin from 'Nabisco/app/HubSpotOnlyAppCookieLogin';
import { useMicrosoftLogin } from '../../hooks/useMicrosoftLogin';
import LoginLoading from '../LoginLoading';
import SignupLink from '../SignupLink';
import GoogleLoginComponent from './GoogleLoginComponent';
import RemeberMeCheckbox from './RememberMeCheckbox';
import SubmitButton from './SubmitButton';
import PasswordInputField from './PasswordInputForm';
import SingleSignOnButton from './SingleSignOnButton';
import { getStagedEmail, getStagedRememberLogin, getLoginRedirectUrl, getAllowFbSignup, getHideSignupLink, getSignupParams, getUseDark, getLoginPortalId, isSsoRequired, isSsoAvailable, getSsoError, isGoogleRecommended, isMicrosoftRecommended, getRequiredLoginType } from '../../selectors/LoginRequirement';
import { trimEmail, isValidEmail } from '../../lib/LoginUtils';
import ErrorMessage from '../ErrorMessage';
import IE11Alert from '../alerts/IE11Alert';
import TokenExpiredAlert from '../alerts/TokenExpiredAlert';
import TwoFactorAuthStatuses from '../../constants/TwoFactorAuthStatuses';
import SsoRequiredAuthStatuses from '../../constants/SsoRequiredAuthStatuses';
import ConfirmToLoginStatuses from '../../constants/ConfirmToLoginAuthStatuses';
import { setTwoFactorMethod, setCredentials } from '../../actions';
import { loginWithSso } from '../../actions/ssoLogin';
import { SMART_LOGIN_REDIRECT } from '../../constants/SmartLogin';
import { MicrosoftLoginButton } from '../microsoft/MicrosoftLoginButton';
import LoginFailureHandlerFactory from '../../lib/LoginFailureHandlerFactory';
import LoginDao from '../../data/LoginDao';
const loginDao = new LoginDao();
const appCookieLogin = new HubSpotOnlyAppCookieLogin({
  loginRequest: loginDao.uiLogin.bind(loginDao),
  passThroughFallback: true
});
import styled from 'styled-components';
const StyledEmailDiv = styled.div.withConfig({
  displayName: "SelectLoginPage__StyledEmailDiv",
  componentId: "rojknk-0"
})(["font-size:22px;font-weight:500;"]);
const FormWrapper = styled.div.withConfig({
  displayName: "SelectLoginPage__FormWrapper",
  componentId: "rojknk-1"
})(["height:100%;margin-left:12px;margin-right:12px;"]);

const SelectLoginPage = props => {
  const dispatch = useDispatch();
  const email = useSelector(getStagedEmail);
  const stagedRememberLogin = useSelector(getStagedRememberLogin);
  const loginRedirectUrl = useSelector(getLoginRedirectUrl);
  const loginPortalId = useSelector(getLoginPortalId);
  const allowFbSignup = useSelector(getAllowFbSignup);
  const hideSignupLink = useSelector(getHideSignupLink);
  const signupParams = useSelector(getSignupParams);
  const useDark = useSelector(getUseDark);
  const googleRecommended = useSelector(isGoogleRecommended);
  const ssoRequired = useSelector(isSsoRequired);
  const ssoAvailable = useSelector(isSsoAvailable);
  const ssoError = useSelector(getSsoError);
  const requiredLoginType = useSelector(getRequiredLoginType);
  const microsoftRecommended = useSelector(isMicrosoftRecommended);
  const query = parseQuery(props.location.search.substr(1));
  const queryParamError = query['loginError'] || '';
  const [rememberLogin, setRememberLogin] = useState(stagedRememberLogin);
  const [submitting, setSubmitting] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isPasswordAutofilled, setIsPasswordAutofilled] = useState(false);
  const [isHidden, setIsHidden] = useState(googleRecommended || microsoftRecommended);
  const {
    loginWithMicrosoft
  } = useMicrosoftLogin();
  useEffect(() => {
    if (googleRecommended || microsoftRecommended) {
      setTimeout(() => {
        setIsHidden(false);
      }, 1000);
    }
  }, [googleRecommended, microsoftRecommended]);
  useEffect(() => {
    if (ssoRequired) {
      dispatch(loginWithSso({
        email,
        loginPortalId,
        loginRedirectUrl,
        history: props.history
      }));
    }
  }, [ssoRequired, email, loginPortalId, loginRedirectUrl, props.history, dispatch]);
  useEffect(() => {
    if (!email) {
      const nextPath = `${SMART_LOGIN_REDIRECT}${stringify(window.location.search)}`;
      props.history.push(nextPath);
    }
  }, [email, props.history]);
  useEffect(() => {
    setTimeout(() => {
      try {
        setIsPasswordAutofilled(document.querySelectorAll('#username:-webkit-autofill').length > 0); // eslint-disable-next-line no-empty
      } catch (ex) {}
    }, 500); // This allows automation to hook into the page for easy login. (For example speedcurve)

    window._hubspotAutomatedLoginHook = setPassword;
  }, []);

  const twoFactorOrRedirectOnSuccess = responses => {
    const response = responses[0];

    if (response.status === TwoFactorAuthStatuses.TWO_FACTOR_REQUIRED) {
      const {
        token,
        primary,
        secondary,
        backupCodesEnabled
      } = response;
      dispatch(setTwoFactorMethod(token, primary, secondary, backupCodesEnabled));
      const nextPath = `/two-factor?${stringify({
        loginPortalId,
        loginRedirectUrl
      })}`;
      props.history.push(nextPath);
    } else if (response.status === SsoRequiredAuthStatuses.SSO_LOGIN_REQUIRED) {
      const {
        hubId,
        requiredBy
      } = response;
      const nextPath = `/sso?${stringify({
        loginPortalId: hubId,
        required: true,
        requiredBy,
        loginRedirectUrl
      })}`;
      props.history.push(nextPath);
    } else {
      appCookieLogin.redirectOnLoginSuccess(responses);
    }
  };

  const confirmToLoginOrGenericError = (response, triggerReset) => {
    if (response.responseJSON && response.responseJSON.status === ConfirmToLoginStatuses.SUSPICIOUS_USER_MUST_CONFIRM) {
      const nextPath = `/confirm-to-login?${stringify({
        loginRedirectUrl
      })}`;
      props.history.push(nextPath);
      return Promise.resolve();
    } else {
      return LoginFailureHandlerFactory.create(triggerReset, props.history.push)(response);
    }
  };

  const triggerReset = err => {
    setError(err);
    setSubmitting(false);
  };

  const loginSubmit = credentials => {
    const credentialParams = Object.assign({}, credentials, {
      loginPortalId,
      loginRedirectUrl
    });
    dispatch(setCredentials(credentialParams));
    appCookieLogin.login(credentialParams).then(twoFactorOrRedirectOnSuccess).catch(err => confirmToLoginOrGenericError(err, triggerReset));
  };

  const onFormSubmit = e => {
    e.preventDefault();
    const data = {
      email,
      password,
      rememberLogin
    };
    data.email = trimEmail(email);
    loginSubmit(data);
    setSubmitting(true);
  };

  const isSubmittable = () => {
    const emailSatisfied = !!email && isValidEmail(email);
    const passwordSatisfied = isPasswordAutofilled || !!password && password.length >= 6;
    return emailSatisfied && passwordSatisfied;
  };

  const submittable = !submitting && isSubmittable();

  const changeEmail = () => {
    const nextPath = `${SMART_LOGIN_REDIRECT}${stringify(window.location.search)}`;
    props.history.push(nextPath, {
      isChangeEmail: true
    });
  };

  if (ssoRequired && !ssoError) {
    return /*#__PURE__*/_jsx(LoginLoading, {});
  }

  const renderPasswordInputForm = () => {
    return /*#__PURE__*/_jsxs("form", {
      id: "hs-login",
      onSubmit: onFormSubmit,
      children: [/*#__PURE__*/_jsx(RemeberMeCheckbox, {
        checked: rememberLogin,
        onChange: () => setRememberLogin(!rememberLogin),
        disabled: submitting
      }), /*#__PURE__*/_jsx("input", {
        type: "email",
        hidden: true,
        value: email,
        readOnly: true
      }), /*#__PURE__*/_jsx(PasswordInputField, {
        useDark: useDark,
        onChange: setPassword,
        password: password,
        submitting: submitting
      }), !googleRecommended && /*#__PURE__*/_jsx(ErrorMessage, {
        error: error || ssoError
      }), /*#__PURE__*/_jsx(TokenExpiredAlert, {}), /*#__PURE__*/_jsx(SubmitButton, {
        submitting: submitting,
        disabled: !submittable
      })]
    });
  };

  const renderPasswordInputOrLink = () => {
    if (googleRecommended && !isHidden) {
      return /*#__PURE__*/_jsx(UIAccordionItem, {
        defaultOpen: false,
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "login.smartLogin.passwordForGoogle"
        }),
        Content: renderPasswordInputForm
      });
    } else if (requiredLoginType === 'HUBSPOT' || requiredLoginType === 'SSO') {
      return renderPasswordInputForm();
    } else {
      return /*#__PURE__*/_jsx("div", {});
    }
  };

  return /*#__PURE__*/_jsx(NavMarker, {
    name: "SL_SELECT_LOGIN_SUCCESS",
    children: /*#__PURE__*/_jsxs(FormWrapper, {
      children: [!hideSignupLink && /*#__PURE__*/_jsx(SignupLink, {
        isFbSignin: allowFbSignup,
        signupParams: signupParams,
        loginRedirectUrlQueryParam: loginRedirectUrl
      }), /*#__PURE__*/_jsx(IE11Alert, {}), /*#__PURE__*/_jsx(ErrorMessage, {
        error: queryParamError
      }), /*#__PURE__*/_jsxs("div", {
        className: "m-top-10",
        children: [/*#__PURE__*/_jsx(FormattedMessage, {
          message: "login.smartLogin.welcome"
        }), /*#__PURE__*/_jsxs(UIFlex, {
          direction: "row",
          align: "end",
          justify: "between",
          children: [/*#__PURE__*/_jsx(StyledEmailDiv, {
            className: "m-bottom-0",
            children: /*#__PURE__*/_jsx(UITruncateString, {
              maxWidth: 320,
              children: email
            })
          }), /*#__PURE__*/_jsx(UIButton, {
            use: "link",
            onClick: changeEmail,
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "login.smartLogin.changeEmail"
            })
          })]
        }), /*#__PURE__*/_jsx(HR, {
          distance: "small"
        })]
      }), googleRecommended && /*#__PURE__*/_jsx(GoogleLoginComponent, {
        loginPortalId: loginPortalId,
        loginRedirectUrl: loginRedirectUrl,
        triggerReset: triggerReset,
        history: props.history
      }), microsoftRecommended && /*#__PURE__*/_jsx(MicrosoftLoginButton, {
        onClick: loginWithMicrosoft,
        smartLogin: true
      }), ssoAvailable && /*#__PURE__*/_jsx(SingleSignOnButton, {
        email: email,
        loginPortalId: loginPortalId,
        loginRedirectUrl: loginRedirectUrl,
        history: props.history
      }), renderPasswordInputOrLink()]
    })
  });
};

export default SelectLoginPage;